import { useEffect, useState, type ReactNode } from "react";

type AuthConfig = {
  mode: "local" | "session" | "oidc";
  client_id?: string;
  authorization_endpoint?: string;
  token_endpoint?: string;
  redirect_uri?: string;
};

const API_BASE = import.meta.env.VITE_API_BASE_URL ?? "";
const apiUrl = (path: string) => {
  const base = API_BASE.replace(/\/$/, "");
  if (base.endsWith("/api") && path.startsWith("/api/")) return `${base}${path.slice(4)}`;
  return `${base}${path}`;
};

const b64url = (bytes: ArrayBuffer) =>
  btoa(String.fromCharCode(...new Uint8Array(bytes)))
    .replaceAll("+", "-")
    .replaceAll("/", "_")
    .replaceAll("=", "");

const randomVerifier = () => {
  const bytes = crypto.getRandomValues(new Uint8Array(48));
  return b64url(bytes.buffer);
};

async function loadConfig(): Promise<AuthConfig> {
  const response = await fetch(apiUrl("/api/auth/config"));
  if (!response.ok) throw new Error("Authentication configuration is unavailable.");
  return response.json() as Promise<AuthConfig>;
}

async function beginLogin(config: AuthConfig) {
  if (!config.authorization_endpoint || !config.client_id || !config.redirect_uri) {
    throw new Error("OIDC authorization configuration is incomplete.");
  }
  const verifier = randomVerifier();
  const challenge = b64url(await crypto.subtle.digest("SHA-256", new TextEncoder().encode(verifier)));
  const state = randomVerifier();
  sessionStorage.setItem("agent15-pkce-verifier", verifier);
  sessionStorage.setItem("agent15-oidc-state", state);
  const url = new URL(config.authorization_endpoint);
  url.searchParams.set("response_type", "code");
  url.searchParams.set("client_id", config.client_id);
  url.searchParams.set("redirect_uri", config.redirect_uri);
  url.searchParams.set("scope", "openid email profile");
  url.searchParams.set("code_challenge_method", "S256");
  url.searchParams.set("code_challenge", challenge);
  url.searchParams.set("state", state);
  window.location.assign(url);
}

async function completeLogin(config: AuthConfig) {
  const params = new URLSearchParams(window.location.search);
  const code = params.get("code");
  const state = params.get("state");
  const expectedState = sessionStorage.getItem("agent15-oidc-state");
  const verifier = sessionStorage.getItem("agent15-pkce-verifier");
  if (!code || !state || state !== expectedState || !verifier) throw new Error("OIDC callback validation failed.");
  if (!config.token_endpoint || !config.client_id || !config.redirect_uri) throw new Error("OIDC token configuration is incomplete.");
  const body = new URLSearchParams({
    grant_type: "authorization_code",
    client_id: config.client_id,
    redirect_uri: config.redirect_uri,
    code,
    code_verifier: verifier,
  });
  const response = await fetch(config.token_endpoint, {
    method: "POST",
    headers: { "Content-Type": "application/x-www-form-urlencoded" },
    body,
  });
  if (!response.ok) throw new Error("OIDC token exchange failed.");
  const tokens = await response.json() as { id_token?: string };
  if (!tokens.id_token) throw new Error("OIDC provider did not return an ID token.");
  sessionStorage.setItem("agent15-id-token", tokens.id_token);
  sessionStorage.removeItem("agent15-pkce-verifier");
  sessionStorage.removeItem("agent15-oidc-state");
  window.history.replaceState({}, "", "/dashboard");
}

export function AuthGate({ children }: { children: ReactNode }) {
  const localBuild = import.meta.env.DEV || import.meta.env.VITE_AUTH_MODE === "local";
  const [state, setState] = useState<"loading" | "authenticated" | "anonymous" | "error">(
    localBuild ? "authenticated" : "loading",
  );
  const [config, setConfig] = useState<AuthConfig | null>(null);

  useEffect(() => {
    if (localBuild) return;
    void loadConfig().then(async (loaded) => {
      setConfig(loaded);
      if (loaded.mode === "local" || loaded.mode === "session") return setState("authenticated");
      if (window.location.pathname === "/auth/callback") await completeLogin(loaded);
      setState(sessionStorage.getItem("agent15-id-token") ? "authenticated" : "anonymous");
    }).catch(() => setState("error"));
  }, [localBuild]);

  if (state === "authenticated") return <>{children}</>;
  if (state === "loading") return <main className="auth-gate"><p>Validating controlled access…</p></main>;
  if (state === "error") return <main className="auth-gate"><h1>Authentication unavailable</h1><p>The trusted identity service could not be validated.</p></main>;
  return <main className="auth-gate"><section className="auth-card"><div className="auth-brand"><span className="auth-mark">M</span><span><strong>MEDTL</strong><small>Regulatory Auditor</small></span></div><div className="auth-eyebrow">CONTROLLED STAGING ACCESS</div><h1>Welcome back</h1><p>Sign in to review controlled evidence, regulatory requirements and audit findings.</p><div className="auth-trust"><span aria-hidden="true">✓</span><span>Protected by your organization’s identity provider</span></div><button className="button primary auth-button" onClick={() => config && void beginLogin(config)}>Sign in securely</button><small className="auth-note">You’ll be redirected to the secure Cognito sign-in page.</small></section></main>;
}
